import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";

// --- Styled components -------------------------------------------------------

const Container = styled.div`
  padding: 1.5em;
  background: transparent;
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 1.5em;
  border-radius: 4px;
  max-width: 520px;
`;

const Field = styled.div`
  margin-bottom: 1.2em;

  label {
    display: block;
    margin-bottom: 0.4em;
    font-size: 0.95em;
    color: ${({ colors }) => colors.labelText};
    font-weight: bold;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.6em;
  font-size: 1em;
  border: 2px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 4px;
  background-color: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.labelText};
  box-sizing: border-box;
`;

const WarningBox = styled.div`
  margin-bottom: 1.2em;
  padding: 0.8em 1em;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 0.9em;
  color: #856404;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  margin-bottom: 1.2em;
  padding: 0.8em 1em;
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  font-size: 0.9em;
  color: #0c5460;
`;

const ErrorText = styled.p`
  color: #c0392b;
  font-size: 0.88em;
  margin: 0.3em 0 0;
`;

const SubmitRow = styled.div`
  margin-top: 1.2em;

  button[type="submit"] {
    padding: 0.6em 1.4em;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 1em;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

// --- Main component ----------------------------------------------------------

export default function MergeOrganizationsContent({ colors, onCancel }) {
  const subscription = useSelector((s) => s.auth.selected_subscriptions) || {};
  const invoiceId = subscription.invoice_id;

  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  const [primaryOrgId, setPrimaryOrgId] = useState("");
  const [secondaryOrgId, setSecondaryOrgId] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [submitting, setSubmitting] = useState(false);

  // Fetch all schools for this invoice once on mount / when invoiceId changes
  useEffect(() => {
    if (!invoiceId) return;
    setSchools([]);
    setPrimaryOrgId("");
    setSecondaryOrgId("");
    setLoadingSchools(true);
    API.adminSearchOrgsForMerge({ invoice_id: invoiceId })
      .then((resp) => {
        if (resp?.status === "Success") {
          setSchools(resp.organizations || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingSchools(false));
  }, [invoiceId]);

  const primarySchool = schools.find(
    (s) => String(s.org_id) === String(primaryOrgId),
  );
  const secondarySchool = schools.find(
    (s) => String(s.org_id) === String(secondaryOrgId),
  );

  const sameOrgError =
    primaryOrgId && secondaryOrgId && primaryOrgId === secondaryOrgId;
  const canSubmit =
    !!invoiceId &&
    primaryOrgId &&
    secondaryOrgId &&
    !sameOrgError &&
    !submitting;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const resp = await API.adminMergeOrganizations({
        invoice_id: invoiceId,
        primary_org_id: primaryOrgId,
        secondary_org_id: secondaryOrgId,
      });
      const status = resp?.status === "Success" ? "Success" : "Failed";
      setModalTitle(status === "Success" ? "Success" : "Error");
      setModalMessage(
        resp?.message ||
          (status === "Failed"
            ? "Failed to merge schools."
            : "Schools merged successfully."),
      );
      setModalStatus(status);
      if (status === "Success") {
        // Remove the merged school from the list and reset selections
        setSchools((prev) =>
          prev.filter((s) => String(s.org_id) !== String(secondaryOrgId)),
        );
        setPrimaryOrgId("");
        setSecondaryOrgId("");
      }
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("An unexpected error occurred.");
      setModalStatus("Failed");
    } finally {
      setSubmitting(false);
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  const confirmMessage =
    primarySchool && secondarySchool
      ? `WARNING: This action is irreversible.\n\n` +
        `"${secondarySchool.org_display}" (ID: ${secondarySchool.org_id}) ` +
        `will be permanently merged into ` +
        `"${primarySchool.org_display}" (ID: ${primarySchool.org_id}).\n\n` +
        `All teachers linked to the secondary school will be re-assigned to the primary. ` +
        `The secondary school record for this invoice will be deleted.`
      : "";

  if (!invoiceId) {
    return (
      <Container>
        <InfoBox>
          No active subscription selected. Please select a subscription first to
          use Merge Schools.
        </InfoBox>
      </Container>
    );
  }

  return (
    <Container>
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Merge Schools"
        message={confirmMessage}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
        single
      />

      <Form onSubmit={onSubmit} colors={colors}>
        <WarningBox>
          <strong>Warning!</strong> Merging schools is permanent and cannot be
          undone. The secondary school will be removed from this invoice and its
          teachers will be re-assigned to the primary school.
        </WarningBox>

        <Field colors={colors}>
          <label>Primary School (kept after merge)</label>
          <StyledSelect
            colors={colors}
            value={primaryOrgId}
            onChange={(e) => setPrimaryOrgId(e.target.value)}
            disabled={loadingSchools}
          >
            <option value="">
              {loadingSchools
                ? "Loading schools..."
                : "-- Select primary school --"}
            </option>
            {schools
              .filter((s) => String(s.org_id) !== String(secondaryOrgId))
              .map((s) => (
                <option key={s.school_id} value={s.org_id}>
                  {s.org_display}
                </option>
              ))}
          </StyledSelect>
        </Field>

        <Field colors={colors}>
          <label>Secondary School (will be deleted)</label>
          <StyledSelect
            colors={colors}
            value={secondaryOrgId}
            onChange={(e) => setSecondaryOrgId(e.target.value)}
            disabled={loadingSchools}
          >
            <option value="">
              {loadingSchools
                ? "Loading schools..."
                : "-- Select secondary school --"}
            </option>
            {schools
              .filter((s) => String(s.org_id) !== String(primaryOrgId))
              .map((s) => (
                <option key={s.school_id} value={s.org_id}>
                  {s.org_display}
                </option>
              ))}
          </StyledSelect>
        </Field>

        {sameOrgError && (
          <ErrorText>
            Primary and Secondary schools must be different.
          </ErrorText>
        )}

        <SubmitRow colors={colors}>
          <button type="submit" disabled={!canSubmit}>
            {submitting ? "Merging..." : "Merge Schools"}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                marginLeft: "0.8em",
                background: "none",
                border: "1px solid #ccc",
                borderRadius: 4,
                padding: "0.6em 1.2em",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </SubmitRow>
      </Form>
    </Container>
  );
}
